import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFeathers, isPermitted, researcherPermissionsIncludingAndAfter } from '../../app/util';
import {
  DataField,
  FieldInlineEdit,
  LoadingSpinner,
  DistrictSelectInput,
  OfficeSelectInput,
  PartySelect,
  UpdateRaceElection
} from '../index';
import InputList from '../InputList';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useSelector } from 'react-redux';
import { ReactComponent as WarningIcon } from '../../images/yellow-warning.svg';
import { ReactComponent as GreenCheck } from '../../images/green-check.svg';
import { IconButton, Tooltip, Typography } from '@mui/material';
import styled from 'styled-components';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ClearIcon from '@mui/icons-material/Clear';
import Checkbox from '@mui/material/Checkbox';

const seatIdentifierTypeWritten = (seatIdentifierType) => {
  switch (seatIdentifierType) {
    case 'incumbentName':
      return 'Incumbent';
    case 'seat':
      return 'Seat';
    case 'post':
      return 'Post';
    case 'division':
      return 'Division';
    case 'position':
      return 'Position';
    default:
      return '--';
  }
}

const contestedStatusWritten = (uncontestedDetailed) => {
  switch (uncontestedDetailed) {
    case 'contested':
      return 'Yes';
    case 'uncontested':
      return 'Uncontested';
    case 'under-contested':
      return 'Under-contested';
    default:
      return '--';
  }
}

const permissionsOrdered = ['researcher', 'reviewer', 'publisher', 'editor', 'admin', 'super-admin'];
const RaceDataFieldEditor = ({
  raceId,
  raceData,
  officeData,
  onChangeRace,
  onError,
  style,
  className
}) => {
  const feathers = useFeathers();
  const user = useSelector(state => state.user);
  const elections = useSelector(state => state.elections.byKey);
  const [ error, setError ] = useState(null);

  const electionKey = raceData?.election;
  const electionData = electionKey ? elections[electionKey] : null;
  const electionOver = electionData?.status === 'results-certified';

  const draftAppearsPublished =
    raceData?.coveragePlan === 'no-coverage' || raceData?.synced;

  const descendantOf = useMemo(
    () => electionData?.districts,
    [electionData]
  )

  const [ stateElectionConfig, setStateElectionConfig ] = useState(null);
  useEffect(() => {
    if (!feathers || !electionData?.stateCode) {
      setStateElectionConfig(null);
      return;
    }
    feathers.getService('state-election-configurations').get(electionData.stateCode.toUpperCase())
      .then(setStateElectionConfig)
      .catch(() => setStateElectionConfig(null));
  }, [feathers, electionData?.stateCode]);

  const [ editingDistrict, setEditingDistrict ] = useState(false);
  const [ newDistrictInput, setNewDistrictInput ] = useState(null);
  
  const [ editingElection, setEditingElection ] = useState(false)
  const [ newAdditionalDistrictsInput, setNewAdditionalDistrictsInput ] = useState(null);
  const [ editingAdditionalDistricts, setEditingAdditionalDistricts ] = useState(false);

  // Position data: seat identifier/type and special election
  const [ editingSeatIdentifier, setEditingSeatIdentifier ] = useState(false);
  const [ seatIdentifierActive, setSeatIdentifierActive ] = useState(raceData?.positionData?.seatIdentifier || null);
  const [ seatIdentifierTypeActive, setSeatIdentifierTypeActive ] = useState(raceData?.positionData?.seatIdentifierType || '');
  const [ clearingSeatIdentifier, setClearingSeatIdentifier ] = useState(false);
  const saveSeatIdentifier = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        positionData: {
          ...(raceData?.positionData || {}),
          seatIdentifier: seatIdentifierActive || null,
          seatIdentifierType: seatIdentifierTypeActive || null
        }
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingSeatIdentifier(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }

  const clearSeatIdentifier = async () => {
    setClearingSeatIdentifier(true);
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        positionData: {
          ...(raceData?.positionData || {}),
          seatIdentifier: null,
          seatIdentifierType: null
        }
      });
      setSeatIdentifierActive(null);
      setSeatIdentifierTypeActive('');
      if (onChangeRace) onChangeRace(updateRes);
      setEditingSeatIdentifier(false);
    } catch(err) {
      console.log(err);
      setError(err);
    } finally {
      setClearingSeatIdentifier(false);
    }
  }

  const [ editingSpecialElection, setEditingSpecialElection ] = useState(false);
  const [ specialElectionActive, setSpecialElectionActive ] = useState(!!(raceData?.position?.special));
  const saveSpecialElection = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        positionData: {
          ...(raceData?.positionData || {}),
          special: !!specialElectionActive
        }
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingSpecialElection(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }

  const [ entityMatchInputs, setEntityMatchInputs ] = useState([]);
  const [ editingEntityMatchInputs, setEditingEntityMatchInputs ] = useState(false);
  const saveEntityMatchInputs = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        entityMatchInputs: entityMatchInputs,
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingEntityMatchInputs(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }

  const appearsOnBallotAs = useMemo(() => {
    if (!raceData) return ''
    const { entityMatchInputs } = raceData;
    if (!entityMatchInputs?.length) {
      return ''
    } else if (entityMatchInputs.length === 1) {
      return `${entityMatchInputs[0]}`
    } else {
      return entityMatchInputs.slice(0, -1).join(', ') + ' or ' + entityMatchInputs.slice(-1)
    }
  }, [raceData])

  const onChangeDistrict = (e) => setNewDistrictInput(e.target.value)
  const onChangeAdditionalDistricts = e => setNewAdditionalDistrictsInput(e.target.value)
  const saveDistrict = async () => {
    // only save the inputs being edited
    const requestBody = {}
    if (newDistrictInput && newDistrictInput._id) {
      requestBody.district = newDistrictInput._id
      cancelDistrict()
    } else if (newAdditionalDistrictsInput) {
      requestBody.additionalDistricts = newAdditionalDistrictsInput.map(d => d._id)
      cancelAdditionalDistricts()
    } else {
      return
    }

    try {
      const updateRes = await feathers.getService('races').patch(raceId, requestBody)
      if (onChangeRace) onChangeRace(updateRes);
      setEditingDistrict(false)
      setEditingAdditionalDistricts(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }

  const cancelDistrict = () => {
    setEditingDistrict(false)
    setNewDistrictInput(null)
  }
  const cancelAdditionalDistricts = () => {
    setEditingAdditionalDistricts(false)
    setNewAdditionalDistrictsInput(null)
  }

  const [ editingRetention, setEditingRetention ] = useState(false);
  const [ retentionActive, setRetentionActive ] = useState(raceData?.retention)
  const saveRetention = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        retention: retentionActive
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingRetention(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }


  const [ editingParty, setEditingParty ] = useState(false);
  const [ partyActive, setPartyActive ] = useState(raceData?.party)

  const saveParty = async () => {
    let $suppress409 = false;
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        party: partyActive
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingParty(false)
      return;
    } catch(err) {
      console.log(err.code)
      if(err.code === 409) {
        $suppress409 = true;
      } else {
        setError(err)
      }
    }

    if($suppress409) {
      // try again with $suppress409
      try {
        const updateRes = await feathers.getService('races').patch(raceId, {
          party: partyActive,
          $suppress409: true
        })
        if (onChangeRace) onChangeRace(updateRes);
        setEditingParty(false)
        return;
      } catch(err) {
        console.log(err)
        setError(err)
      }
    } 
  }

  const [ editingMaxChoices, setEditingMaxChoices ] = useState(false);
  const [ maxChoicesActive, setMaxChoicesActive ] = useState(1);
  const cancelMaxChoices = () => {
    setEditingMaxChoices(false)
    setMaxChoicesActive(raceData.maxChoices)
  }

  const saveMaxChoices = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        maxChoices: maxChoicesActive
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingMaxChoices(false);
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }


  const [ editingCoveragePlan, setEditingCoveragePlan ] = useState(false);
  const [ coveragePlanActive, setCoveragePlanActive ] = useState(null);
  const cancelCoveragePlan = () => {
    setEditingCoveragePlan(false)
    setCoveragePlanActive(raceData.coveragePlan)
  }

  const saveCoveragePlan = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        coveragePlan: coveragePlanActive
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingCoveragePlan(false);
    } catch(err) {
      console.log(err)
    }
  }

  const [ editingRankedChoice, setEditingRankedChoice ] = useState(false);
  const [ rankedChoiceActive, setRankedChoiceActive ] = useState(raceData?.rankedChoice || { enabled: false, maxChoices: 5 });
  const saveRankedChoice = async () => {
    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        rankedChoice: rankedChoiceActive
      })
      if (onChangeRace) onChangeRace(updateRes);
      setEditingRankedChoice(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }

  const [ editingRunningMates, setEditingRunningMates ] = useState(false);
  const [ runningMatesActive, setRunningMatesActive ] = useState(raceData?.runningMates || { enabled: false, secondaryOffice: null });
  const [ secondaryOfficeData, setSecondaryOfficeData ] = useState(null);
  const [ secondaryOfficeActive, setSecondaryOfficeActive ] = useState(null);
  const saveRunningMates = async () => {
    const secondaryOffice = secondaryOfficeActive?.key || runningMatesActive.secondaryOffice;
    if(runningMatesActive.enabled && !secondaryOffice) {
      setError(new Error('Select a secondary office before enabling running mates.'));
      return;
    }

    try {
      const updateRes = await feathers.getService('races').patch(raceId, {
        runningMates: {
          enabled: runningMatesActive.enabled,
          secondaryOffice: runningMatesActive.enabled ? secondaryOffice : null
        }
      })
      if(onChangeRace) onChangeRace(updateRes);
      setEditingRunningMates(false)
    } catch(err) {
      console.log(err)
      setError(err)
    }
  }

  useEffect(() => {
    const secondaryOffice = raceData?.runningMates?.secondaryOffice;
    if(!feathers || !secondaryOffice) {
      setSecondaryOfficeData(null);
      setSecondaryOfficeActive(null);
      return;
    }

    let cancelled = false;
    feathers.getService('offices').get(secondaryOffice)
      .then(office => {
        if(cancelled) return;
        const officeOption = { text: office.name, ...office };
        setSecondaryOfficeData(officeOption);
        setSecondaryOfficeActive(officeOption);
      })
      .catch(() => {
        if(cancelled) return;
        setSecondaryOfficeData(null);
        setSecondaryOfficeActive(null);
      });

    return () => {
      cancelled = true;
    };
  }, [feathers, raceData?.runningMates?.secondaryOffice]);

  const getNewDistrictInput = useMemo(() => {
    if (raceData?.originalDistrict) {
      return { text: raceData?.originalDistrict?.longName, ...raceData?.originalDistrict }
    }
    return { text: raceData?.district?.longName, ...raceData?.district }
    }, [raceData?.originalDistrict, raceData?.district])

  useEffect(() => {
    if (!raceData) return;
    setMaxChoicesActive(raceData.maxChoices || 1);
    setCoveragePlanActive(raceData.coveragePlan);
    setPartyActive(raceData.party);
    setRankedChoiceActive(raceData.rankedChoice || { enabled: false, maxChoices: 5 });
    setRunningMatesActive(raceData.runningMates || { enabled: false, secondaryOffice: null });
    setSeatIdentifierActive(raceData?.positionData?.seatIdentifier || null);
    setSeatIdentifierTypeActive(raceData?.positionData?.seatIdentifierType || '');
    setSpecialElectionActive(!!(raceData?.positionData?.special));
  }, [raceData])

  const errorMessage = error ? (error?.message || 'An error occurred. Please try again later.') : null;

  return (
    <Wrapper>
      <Section>
        <Typography variant='h3'>Ballot display</Typography>
        <div className='seperator' />
        <DataField title='Office'>
          <Link to={`/offices/${officeData?.key || raceData.office}`} target='_blank'>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <Typography variant='body1'>{officeData?.name || raceData.officeName || '--'}</Typography>
              <OpenInNewIcon style={{ width: '15px', height: '15px' }}/>
            </div>
          </Link>
        </DataField>
        {
          editingMaxChoices
          ?
            <FieldInlineEdit
              inputComponent={
                <Select
                  value={maxChoicesActive}
                  onChange={e => setMaxChoicesActive(e.target.value)}
                >
                  {
                    [...Array(20).keys()].map(v => (
                      <MenuItem key={v} value={v+1}>{v+1}</MenuItem>
                    ))
                  }
                </Select>
              }
              onCancel={cancelMaxChoices}
              onSave={saveMaxChoices}
              label='Plurality'
            />
          :
            <DataField
              title='Plurality'
              onEdit={
                isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer'])
                ?
                  () => {
                    setEditingMaxChoices(true)
                  }
                : null
              }
            >
              <div>
                <Typography variant={'body1'}>
                  {raceData.maxChoices} candidates
                </Typography>
              </div>
            </DataField>
        }
        {
          editingRankedChoice
          ?
            <FieldInlineEdit
              inputComponent={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      checked={rankedChoiceActive.enabled}
                      onChange={(e) => setRankedChoiceActive({
                        ...rankedChoiceActive,
                        enabled: e.target.checked
                      })}
                    />
                    <Typography variant="body1">Enable Ranked Choice Voting</Typography>
                  </div>
                  {rankedChoiceActive.enabled && (
                    <Select
                      value={rankedChoiceActive.maxChoices}
                      onChange={(e) => setRankedChoiceActive({
                        ...rankedChoiceActive,
                        maxChoices: e.target.value
                      })}
                      style={{ width: '100%' }}
                    >
                      {[...Array(20).keys()].map(v => (
                        <MenuItem key={v} value={v+1}>{v+1} choices</MenuItem>
                      ))}
                    </Select>
                  )}
                </div>
              }
              onCancel={() => {
                setEditingRankedChoice(false)
                setRankedChoiceActive(raceData.rankedChoice || { enabled: false, maxChoices: 5 })
              }}
              onSave={saveRankedChoice}
              label='Ranked Choice Voting'
            />
          :
            <DataField
              title='Ranked Choice Voting'
              onEdit={
                isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer'])
                ?
                  () => {
                    setRankedChoiceActive(raceData.rankedChoice || { enabled: false, maxChoices: 5 })
                    setEditingRankedChoice(true)
                  }
                : null
              }
            >
              <div>
                <Typography variant={'body1'}>
                  {raceData.rankedChoice?.enabled 
                    ? `Enabled (${raceData.rankedChoice.maxChoices} choices)`
                    : 'Disabled'}
                </Typography>
              </div>
            </DataField>
        }
        {
          editingRunningMates
          ?
            <FieldInlineEdit
              inputComponent={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      checked={runningMatesActive.enabled}
                      onChange={(e) => setRunningMatesActive({
                        ...runningMatesActive,
                        enabled: e.target.checked
                      })}
                    />
                    <Typography variant='body1'>Enable running mates</Typography>
                  </div>
                  {runningMatesActive.enabled && (
                    <OfficeSelectInput
                      value={secondaryOfficeActive}
                      onChange={e => {
                        const office = e.target.value;
                        setSecondaryOfficeActive(office);
                        setRunningMatesActive({
                          ...runningMatesActive,
                          secondaryOffice: office?.key || null
                        });
                      }}
                      descendantOf={descendantOf}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
              }
              onCancel={() => {
                setEditingRunningMates(false)
                setRunningMatesActive(raceData.runningMates || { enabled: false, secondaryOffice: null })
                setSecondaryOfficeActive(secondaryOfficeData)
              }}
              onSave={saveRunningMates}
              label='Running mates'
            />
          :
            <DataField
              title='Running mates'
              onEdit={
                isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer'])
                ?
                  () => {
                    setRunningMatesActive(raceData.runningMates || { enabled: false, secondaryOffice: null })
                    setSecondaryOfficeActive(secondaryOfficeData)
                    setEditingRunningMates(true)
                  }
                : null
              }
            >
              <Typography variant='body1'>
                {raceData.runningMates?.enabled
                  ? 'Enabled'
                  : 'Disabled'}
              </Typography>
              {raceData.runningMates?.enabled && raceData.runningMates.secondaryOffice && (
                <Link to={`/offices/${raceData.runningMates.secondaryOffice}`} target='_blank'>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}>
                    <Typography variant='body1'>
                      Secondary office: {secondaryOfficeData?.name || raceData.runningMates.secondaryOffice}
                    </Typography>
                    <OpenInNewIcon style={{ width: '15px', height: '15px' }}/>
                  </div>
                </Link>
              )}
            </DataField>
        }
        {
          raceData.maxChoices === 1 && 
          <>
            {
              editingRetention
              ?
                <FieldInlineEdit
                  inputComponent={
                    <Select
                      value={retentionActive}
                      onChange={e => setRetentionActive(e.target.value)}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  }
                  onCancel={() => setEditingRetention(false)}
                  onSave={saveRetention}
                  label='Retention election'
                />
              :
                <DataField
                  title='Retention election'
                  onEdit={
                    isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer'])
                    ?
                      () => {
                        setEditingRetention(true)
                      }
                    : null
                  }
                >
                  <div>
                    <Typography variant={'body1'}>
                      {raceData.retention ? 'Yes' : 'No'}
                    </Typography>
                  </div>
                </DataField>
            }
          </>
        }
        {
          raceData?.party && (
            <>
            {
              editingParty
                ?
                  <FieldInlineEdit
                    inputComponent={
                      <PartySelect
                        value={partyActive}
                        onChange={setPartyActive}
                        partyOptions={stateElectionConfig?.supportedPartiesForPrimary || []}
                      />
                    }
                    onCancel={() => setEditingParty(false)}
                    onSave={saveParty}
                    label='Party'
                  />
                :
                  <DataField
                    title='Party'
                    onEdit={
                      isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer', 'admin'])
                      ?
                        () => {
                          setEditingParty(true)
                        }
                      : null
                    }
                  >
                    <div>
                      <Typography variant={'body1'}>
                        {raceData.party}
                      </Typography>
                    </div>
                  </DataField>
              }
            </>
          )
        }
        {
          editingDistrict
          ?
            <FieldInlineEdit
              inputComponent={
                <DistrictSelectInput
                  onChange={onChangeDistrict}
                  value={newDistrictInput}
                  districtTypes={officeData?.districtTypes || []}
                  style={{ width: 'calc(100% - 15px)' }}
                  descendantOf={descendantOf}
                />
              }
              onCancel={cancelDistrict}
              onSave={saveDistrict}
              label='District'
            />
          :
            <DataField
              title='District'
              onEdit={
                isPermitted(user, electionOver ? ['super-admin', 'admin', 'editor'] : ['editor', 'super-admin', 'researcher', 'admin', 'reviewer', 'publisher'])
                && editingAdditionalDistricts === false
                ?
                  () => {
                    setNewDistrictInput(getNewDistrictInput)
                    setEditingDistrict(true)
                  }
                : null
              }
            >
              <div>
                <Typography variant={'body1'}>
                  { raceData.originalDistrict?.longName || raceData.district?.longName}
                </Typography>
              </div>
            </DataField>
        }
        {
          editingAdditionalDistricts
          ?
            <FieldInlineEdit
              inputComponent={
                <DistrictSelectInput
                  onChange={onChangeAdditionalDistricts}
                  value={newAdditionalDistrictsInput}
                  districtTypes={officeData?.districtTypes || []}
                  style={{ width: 'calc(100% - 15px)' }}
                  descendantOf={descendantOf}
                  multi={true}
                  seedMultiSearchText={newAdditionalDistrictsInput}
                  primaryDistrictId={raceData.district?._id}
                />
              }
              onCancel={cancelAdditionalDistricts}
              onSave={saveDistrict}
              label='Additional Districts'
            />
          :
            <DataField
              title='Additional Districts'
              onEdit={
                isPermitted(user, electionOver ? ['super-admin', 'admin', 'editor'] : ['editor', 'super-admin', 'researcher', 'admin', 'reviewer', 'publisher'])
                && editingDistrict === false
                ?
                  () => {
                    setNewAdditionalDistrictsInput(raceData.additionalDistricts?.map(d => ({ text: d.longName, ...d })))
                    setEditingAdditionalDistricts(true)
                  }
                : null
              }
            >
              <div>
                {
                  (raceData.additionalDistricts || []).map((district, i) => {
                    return (
                      <Typography variant={'body1'} key={district?._id}>
                        {district?.longName}
                      </Typography>
                    )
                  })
                }
                {
                  (raceData.additionalDistricts || []).length === 0 && (
                    <Typography variant={'body1'}>
                      None
                    </Typography>
                  )
                }
              </div>
            </DataField>
        }
        {
          editingEntityMatchInputs
          ?
            <FieldInlineEdit
              inputComponent={
              <InputList
                value={entityMatchInputs}
                name={'entityMatchInputs'}
                renderItem={(_item, i, ) => (
                <TextField
                  value={entityMatchInputs[i]}
                  onChange={(e) => {
                    const newInputs = entityMatchInputs.slice()
                    newInputs[i] = e.target.value
                    setEntityMatchInputs(newInputs)
                  }}
                  variant='outlined'
                  size='small'
                  style={{margin: '2px 0px', width: 'calc(100% - 16px)'}}
                />
              )}
               onChange={(e) =>{
                 setEntityMatchInputs(e.value || [])
                }}
                onAdd = {e => setEntityMatchInputs(em => [...em, ''])}
               />
              }
              onSave={saveEntityMatchInputs}
              label='Appears on ballot as'
            />
          :
            <DataField
              onEdit={
                isPermitted(user, electionOver ? ['super-admin', 'admin', 'editor'] : ['editor', 'super-admin', 'researcher', 'admin', 'reviewer', 'publisher'])
                ?
                  () => {
                    setEntityMatchInputs(raceData?.entityMatchInputs || [])
                    setEditingEntityMatchInputs(true)
                  }
                : null
              }
              title='Appears on ballot as'
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '4px'}}>
                {
                  (raceData?.entityMatchInputs || [])?.length > 0 && raceData?.entityMatchInputs.map((input, i) => (
                    <Typography
                      key={input || i}
                      variant={'body1'}
                      style={{ lineHeight: '1.1'}}
                    >
                      {input}
                    </Typography>
                  ))
                }
                {
                  (raceData?.entityMatchInputs || [])?.length === 0 && (
                    <Typography variant={'body1'}>
                      --
                    </Typography>
                  )
                }
              </div>
            </DataField>
        }
        {
          editingSeatIdentifier
          ?
            <FieldInlineEdit
              inputComponent={
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', flexGrow: 1 }}>
                  <Select
                    size="small"
                    value={seatIdentifierTypeActive || ''}
                    onChange={(e) => setSeatIdentifierTypeActive(e.target.value || '')}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="incumbentName">{seatIdentifierTypeWritten('incumbentName')}</MenuItem>
                    <MenuItem value="seat">{seatIdentifierTypeWritten('seat')}</MenuItem>
                    <MenuItem value="post">{seatIdentifierTypeWritten('post')}</MenuItem>
                    <MenuItem value="division">{seatIdentifierTypeWritten('division')}</MenuItem>
                    <MenuItem value="position">{seatIdentifierTypeWritten('position')}</MenuItem>
                  </Select>
                  <TextField
                    size="small"
                    value={seatIdentifierActive || ''}
                    onChange={(e) => setSeatIdentifierActive(e.target.value || null)}
                    placeholder={`Enter ${seatIdentifierTypeActive || 'seat'} identifier`}
                  />
                  {
                    (raceData?.positionData?.seatIdentifier || raceData?.positionData?.seatIdentifierType) && (
                      <IconButton
                        aria-label="Clear seat identifier"
                        title="Clear seat identifier"
                        onClick={clearSeatIdentifier}
                        disabled={clearingSeatIdentifier}
                        sx={{ color: 'text.secondary' }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                </div>
              }
              onCancel={() => {
                setEditingSeatIdentifier(false)
                setSeatIdentifierActive(raceData?.positionData?.seatIdentifier || null)
                setSeatIdentifierTypeActive(raceData?.positionData?.seatIdentifierType || '')
              }}
              onSave={saveSeatIdentifier}
              label='Seat identifier'
            />
          :
            <DataField
              title='Seat identifier'
              onEdit={
                isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer'])
                ? () => setEditingSeatIdentifier(true)
                : null
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Typography variant={'body1'}>
                  {raceData?.positionData?.seatIdentifierType ? `${seatIdentifierTypeWritten(raceData?.positionData?.seatIdentifierType)}` : '--'}
                </Typography>
                <Typography variant={'body1'}>
                  {raceData?.positionData?.seatIdentifier ? `${raceData.positionData.seatIdentifier}` : ''}
                </Typography>
              </div>
            </DataField>
        }
        {
          editingSpecialElection
          ?
            <FieldInlineEdit
              inputComponent={
                <Select
                  value={specialElectionActive}
                  onChange={e => setSpecialElectionActive(e.target.value)}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              }
              onCancel={() => {
                setEditingSpecialElection(false)
                setSpecialElectionActive(!!(raceData?.positionData?.special))
              }}
              onSave={saveSpecialElection}
              label='Special election'
            />
          :
            <DataField
              title='Special election'
              onEdit={
                isPermitted(user, ['editor', 'super-admin', 'publisher', 'reviewer'])
                ? () => setEditingSpecialElection(true)
                : null
              }
            >
              <div>
                <Typography variant={'body1'}>
                  {raceData?.positionData?.special ? 'Yes' : 'No'}
                </Typography>
              </div>
            </DataField>
        }
        {
          editingElection
          ?
            <UpdateRaceElection
              onError={setError}
              onSuccess={(newRace) => {
                if (onChangeRace) onChangeRace(newRace);
                setEditingElection(false)
              }}
              redirectOnFinish
              populateOnStart={true}
              race={raceData}
            />
          :
            <DataField
              title='Election'
              onEdit={
                (isPermitted(user, researcherPermissionsIncludingAndAfter('researcher')) && electionData?.partisan)
                ? () => setEditingElection(true)
                : null
              }
            >
              <Typography variant='body1'>
                {electionData?.name || electionKey || '--'}
              </Typography>
            </DataField>
        }
        <DataField title='Contested'>
          <Typography variant='body1'>
            {contestedStatusWritten(raceData?.uncontestedDetailed)}
          </Typography>
        </DataField>
      </Section>
      <Section>
        <Typography variant='h3'>Status</Typography>
        <div className='seperator' />
        {
          editingCoveragePlan
          ? (
            <FieldInlineEdit
              inputComponent={
                <Select
                  value={coveragePlanActive}
                  onChange={e => setCoveragePlanActive(e.target.value)}
                >
                  <MenuItem value={'auto-coverage'}>Detailed platforms</MenuItem>
                  <MenuItem value={'basic-coverage'}>Basic info</MenuItem>
                  <MenuItem value={'no-coverage'}>No coverage</MenuItem>
                </Select>
              }
              onCancel={cancelCoveragePlan}
              onSave={saveCoveragePlan}
              label='Coverage Plan'
            />
          ) : (
            <DataField title='Coverage Plan' onEdit={isPermitted(user, ['editor','super-admin']) ? () => setEditingCoveragePlan(true) : null}>
              <Typography variant='body1'>
              {
                raceData?.coveragePlan === 'no-coverage'
                ? 'No coverage'
                : (raceData?.coveragePlan === 'basic-coverage'
                  ? 'Basic info'
                  : 'Detailed platforms'
                )
              }
              </Typography>
            </DataField>
          )
        }
        <DataField title='Priority'>
          <Tooltip title={
            raceData?.priorityLevel === 'high'
            ? 'This candidate is a high priority level, and will be viewed by many users.'
            : (raceData?.priorityLevel === 'medium'
              ? 'This candidate is a medium priority level, and will be viewed by some users.'
              : 'This candidate is a low priority level, and will be viewed by few users.'
            )
          }>
            <Typography variant='body1'>
              {
                raceData?.priorityLevel === 'high'
                ? 'High'
                : (raceData?.priorityLevel === 'medium'
                  ? 'Medium'
                  : 'Low'
                )
              }
            </Typography>
          </Tooltip>
        </DataField>
        <DataField title='Status'>
          <SyncContainer>
            {
              draftAppearsPublished
              ? <Tooltip title='All changes made to the draft have been published to the Branch platform.'>
                  <GreenCheck />
                </Tooltip>
              : <Tooltip title='Some changes made to the draft have not been published to the Branch platform.'>
                <WarningIcon/>
              </Tooltip>
            }
            <div style={{ marginTop: '2px' }}>
              <Typography variant={'body1'}>
                {draftAppearsPublished ? 'Draft published' : 'Unpublished changes'}
              </Typography>
            </div>
          </SyncContainer>
        </DataField>
      </Section>
      <Section>
        <Typography variant='h3'>Analytics</Typography>
        <div className='seperator' />
        <DataField title='Expected readers'>
          <Typography variant='body1'>{raceData?.expectedReaders ? `${Math.round(raceData.expectedReaders).toLocaleString()}` : '--'}</Typography>
        </DataField>
        <DataField title='Number of readers'>
          <Typography variant='body1'>{typeof(raceData?.analytics?.views) !== 'undefined' ? `${Math.round(raceData.analytics?.views).toLocaleString()}` : '--'}</Typography>
        </DataField>
        <DataField title='Number of users'>
          <Tooltip title='The number of users that have this race on their ballot'>
            <Typography variant='body1'>{typeof(raceData?.analytics?.onBallot) !== 'undefined' ? `${Math.round(raceData.analytics?.onBallot).toLocaleString()}` : '--'}</Typography>
          </Tooltip>
        </DataField>
      </Section>
      {/*set up a  material ui snackbar popup for error messages*/}
      <Snackbar
        open={error != null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        style={{ position: 'absolute' }}
        onClose={() => setError(undefined)}
      >
        <Alert
          severity="error"
          onClose={() => setError(undefined)}
          elevation={6}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      
    </Wrapper>
  )
}


const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 48px;
`

const SyncContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  svg {
    width: 34px;
    margin-right: 6px;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-left: 16px;
  gap: 20px;

  h3 {
    margin-left: -16px;
    margin-bottom: -8px;
    font-size: 16px;
  }

  .seperator {
    height: 1px;
    width: 100%;
    background-color: #DDDDDD;
    margin: 0px 0 0px -16px;
  }
`

export { RaceDataFieldEditor };